import CustomCheckbox from "@/components/inputs/custom-checkbox";
import CustomInput from "@/components/inputs/custom-input";
import CustomRadioGroup from "@/components/inputs/custom-radio-group";
import type { InputCardOption } from "@/components/inputs/types";
import { Button } from "@/components/ui/button";
import { FieldLegend, FieldSet } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { useVoucherDetails } from "@/hooks/use-voucher-details";
import { MARITAL_STATUSES_OPTIONS } from "@/lib/constants";
import type { ConditionalQuestion } from "@/lib/vouch-flow";
import { ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router";

const QUESTION_FIELDS: Partial<
  Record<ConditionalQuestion, { legend: string; options: InputCardOption[] }>
> = {
  MaritalStatus: {
    legend: "What is your marital status",
    options: MARITAL_STATUSES_OPTIONS,
  },
};

// This form captures details about the person filling the vouch: their name, phone and marital status
export const VoucherDetailsForm = ({
  voucheeName,
}: {
  voucheeName?: string;
}) => {
  const navigate = useNavigate();
  const {
    name,
    maritalStatus,
    visibleQuestions,
    phone,
    fieldError,
    oath,
    handleOathChange,
    handlePhoneChange,
    updateMeta,
    handleContinue,
    isSubmitting,
    isDisabled,
  } = useVoucherDetails();

  const answers: Partial<Record<ConditionalQuestion, string>> = {
    MaritalStatus: maritalStatus,
  };

  return (
    <form
      onSubmit={(event) => handleContinue(event)}
      className="flex w-full flex-col gap-6"
    >
      <div className="flex w-full flex-col gap-2">
        <CustomInput
          label="What's your name?"
          id="name"
          name="Name"
          // leftIcon={UserRound}
          type="text"
          placeholder="First and last name"
          value={name}
          onChange={(e) => updateMeta({ Name: e.target.value })}
          error={fieldError.Name}
        />
        <p className="flex gap-1 text-xs text-muted-foreground sm:items-center">
          <ShieldCheck color="#60407d" className="size-4 shrink-0 sm:size-3" />
          Your name appears with your vouch on {voucheeName}'s profile.
        </p>
      </div>
      {visibleQuestions?.map((question) => {
        const field = QUESTION_FIELDS[question];
        if (!field) return null;
        return (
          <FieldSet key={question} className="w-full">
            <FieldLegend>{field.legend}</FieldLegend>
            <div className="w-full">
              <CustomRadioGroup
                name={question}
                options={field.options}
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
                value={answers[question]}
                onValueChange={(val) =>
                  updateMeta({ [question]: val as string })
                }
              />
            </div>
          </FieldSet>
        );
      })}

      <div className="flex w-full flex-col gap-2">
        <CustomInput
          label="What's your WhatsApp number?"
          // leftIcon={Phone}
          id="phone"
          name="phone"
          type="tel"
          placeholder="+13653550000"
          value={phone}
          onChange={handlePhoneChange}
          error={fieldError.Phone}
        />
        <p className="flex gap-1 text-xs text-muted-foreground sm:items-center">
          <ShieldCheck color="#60407d" className="size-4 shrink-0 sm:size-3" />
          You'll get a code on WhatsApp to confirm it's you. Your number is never shared.
        </p>
      </div>

      <CustomCheckbox
        id="oath"
        name="oath"
        label="I confirm my answers are true and agree to be contacted if Sakeena needs to verify my vouch."
        checked={oath}
        onCheckedChange={(checked) => handleOathChange(Boolean(checked))}
      />

      <div className="grid grid-cols-2 gap-2">
        <Button
          size="xl"
          type="button"
          variant="secondary"
          disabled={isSubmitting}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Button size="xl" type="submit" disabled={isDisabled}>
          {isSubmitting ? (
            <>
              <Spinner data-icon="inline-start" />
            </>
          ) : (
            "Next"
          )}
        </Button>
      </div>
    </form>
  );
};
